<script>
export default {
  name: "DidoMillesimeInputFilterCog",
};
</script>

<script setup>
import { ref, watch } from "vue";
import api from "../../../api";
import { pause } from "../../../utils";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  modelValue: { type: String, required: false, default: "" },
  label: { type: Boolean, required: true },
});

// define watchers
watch(
  () => [props.modelValue],
  () => (inputValue.value = props.modelValue),
);

// define Emits
const emit = defineEmits(["update:model-value"]);

// define reactive data
const inputValue = ref(props.modelValue);
const inputChecking = ref(false);
const inputError = ref(false);
const inputErrorMessage = ref(null);
const dialogOpened = ref(false);
const millesime = ref(null);
const level = ref(null);
const millesimeOptions = ref(api.cogOptions.millesimes);
const levelOptions = ref(api.cogOptions.levels);
const searchOptions = ref([]);
const zoneId = ref(null);

// define methods
const filterSearchText = (value, update, abort) => {
  update(async () => {
    try {
      searchOptions.value = await api.getCogZones(
        props.apiUrl,
        millesime.value,
        level.value,
        value,
      );
    } catch (error) {
      abort();
    }
  });
};
const abortFilterSearchText = () => {};
const openDialog = async () => {
  const zone = await api.getCogZone(props.apiUrl, inputValue.value);
  if (zone) {
    millesime.value = zone.MILLESIME;
    level.value = zone.LEVEL;
    zoneId.value = zone.ID;
    searchOptions.value.push({ label: zone.OFFICIAL_NAME, value: zone.ID });
  } else {
    millesime.value = null;
    level.value = null;
    zoneId.value = null;
    searchOptions.value = [];
  }
};
const closeDialog = () => {
  inputValue.value = zoneId.value || "";
  emit("update:model-value", inputValue.value);
  inputError.value = false;
  inputErrorMessage.value = null;
  dialogOpened.value = false;
};
const checkAndSetValue = async () => {
  if (!inputValue.value) return emit("update:model-value", inputValue.value);
  inputChecking.value = true;
  inputError.value = false;
  inputErrorMessage.value = null;
  await Promise.all([
    api.getCogZone(props.apiUrl, inputValue.value),
    pause(500),
  ]).then(([zone]) => {
    if (zone) {
      emit("update:model-value", inputValue.value);
    } else {
      inputErrorMessage.value = "L'identifiant saisi n'existe pas";
      inputError.value = true;
    }
    inputChecking.value = false;
  });
};
</script>

<template>
  <QInput
    v-model="inputValue"
    dense
    square
    outlined
    :label="label ? 'Identifiant de la zone administrative' : ''"
    stack-label
    class="dido-millesimefilter-cog"
    :loading="inputChecking"
    :error="inputError"
    :error-message="inputErrorMessage"
    hide-bottom-space
    clearable
    clear-icon="mdi-close"
    debounce="500"
    @update:model-value="checkAndSetValue"
  >
    <template #append>
      <QBtn
        round
        dense
        flat
        icon="mdi-form-select"
        @click="dialogOpened = true"
      />
    </template>
  </QInput>
  <QDialog
    v-model="dialogOpened"
    class="dido-millesimefilter-cog-dialog"
    persistent
    transition-show="scale"
    transition-hide="scale"
    @show="openDialog"
  >
    <QCard class="dido-millesimefilter-cog-dialogcard bg-secondary text-white">
      <QCardSection>
        <QBar class="bg-secondary text-overline">
          Sélection d'une zone administrative
          <QSpace />
          <QBtn v-close-popup dense flat icon="mdi-window-close" />
        </QBar>
      </QCardSection>

      <QCardSection class="bg-white">
        <QField
          square
          outlined
          label="Choix du millésime de la zone"
          stack-label
          :value="millesime"
        >
          <template #control>
            <QOptionGroup
              v-model="millesime"
              :options="millesimeOptions"
              color="secondary"
              size="xs"
              inline
              @update:model-value="[(level = null), (zoneId = null)]"
            />
          </template>
        </QField>
        <QSeparator v-if="millesime" spaced="lg" />
        <QField
          v-if="millesime"
          square
          outlined
          label="Choix du niveau géographique de la zone"
          stack-label
          :value="level"
        >
          <template #control>
            <QOptionGroup
              v-model="level"
              :options="levelOptions"
              color="secondary"
              size="xs"
              inline
              @update:model-value="zoneId = null"
            />
          </template>
        </QField>
        <QSeparator v-if="level" spaced="lg" />
        <QSelect
          v-if="level"
          v-model="zoneId"
          square
          outlined
          label="Zone à rechercher"
          stack-label
          map-options
          emit-value
          use-input
          input-debounce="500"
          :options="searchOptions"
          @filter="filterSearchText"
          @filter-abort="abortFilterSearchText"
        />
      </QCardSection>

      <QCardActions align="right" class="bg-white text-secondary">
        <QBtn flat label="OK" @click="closeDialog" />
      </QCardActions>
    </QCard>
  </QDialog>
</template>

<style lang="sass" scoped>
.dido-millesimefilter-cog
  min-width: 150px
.dido-millesimefilter-cog-dialogcard
  width: 80vw !important
  max-width: 80vw !important
</style>
